import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Stethoscope, Users, TrendingUp, Package, FileText, User } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import ProfessionalBadge from '@/components/profile/ProfessionalBadge';

export default function ProfessionalDashboard() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: myPatients = [] } = useQuery({
    queryKey: ['my-patients', user?.id],
    queryFn: async () => {
      const relations = await base44.entities.UserProfessionalRelation.filter({ 
        professional_id: user?.id,
        status: 'active'
      });
      const userIds = relations.map(r => r.user_id);
      const allUsers = await base44.entities.User.list();
      return allUsers.filter(u => userIds.includes(u.id));
    },
    enabled: !!user?.id && user?.is_professional,
  });

  const { data: patientProducts = [] } = useQuery({
    queryKey: ['patient-products', user?.id],
    queryFn: async () => {
      const relations = await base44.entities.UserProfessionalRelation.filter({ 
        professional_id: user?.id,
        status: 'active',
        share_products: true
      });
      const userEmails = [];
      for (const rel of relations) {
        const users = await base44.entities.User.list();
        const patient = users.find(u => u.id === rel.user_id);
        if (patient) userEmails.push(patient.email);
      }
      
      const allProducts = await base44.entities.Product.list('-created_date', 100);
      return allProducts.filter(p => userEmails.includes(p.created_by));
    },
    enabled: !!user?.id && user?.is_professional,
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400">Cargando...</p>
      </div>
    );
  }

  if (!user.is_professional) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso Profesional</h2>
          <p className="text-gray-600 mb-4">Esta sección es solo para profesionales de la salud verificados</p>
          <Link to={createPageUrl('Profile')}>
            <Button>Ir a Mi Perfil</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 pb-20">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-bold text-gray-900 text-lg">Panel Profesional</h1>
                  <ProfessionalBadge user={user} size="sm" />
                </div>
                <p className="text-xs text-gray-400">{user.professional_type || 'Profesional de la salud'}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {!user.professional_verified && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="p-4 bg-amber-50 border-amber-200">
              <p className="text-sm text-amber-700">
                ⏳ Tu perfil profesional está pendiente de verificación administrativa. Una vez verificado, los usuarios podrán elegirte como su profesional de confianza.
              </p>
            </Card>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-600" />
              <p className="text-sm text-gray-500">Pacientes</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{myPatients.length}</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-emerald-600" />
              <p className="text-sm text-gray-500">Productos escaneados</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{patientProducts.length}</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <p className="text-sm text-gray-500">Promedio de salud</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {patientProducts.length > 0 
                ? Math.round(patientProducts.reduce((sum, p) => sum + (p.scaneat_score || 0), 0) / patientProducts.length)
                : '-'}
            </p>
          </Card>
        </div>

        <Tabs defaultValue="patients" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="patients">Mis Pacientes ({myPatients.length})</TabsTrigger>
            <TabsTrigger value="products">Productos Escaneados ({patientProducts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="patients" className="space-y-4">
            {myPatients.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Aún no tienes pacientes</h3>
                <p className="text-gray-600 text-sm">
                  Los usuarios podrán elegirte como su profesional de confianza desde su perfil
                </p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myPatients.map((patient) => {
                  const patientProductsCount = patientProducts.filter(p => p.created_by === patient.email).length;
                  const conditions = [
                    ...(patient.health_conditions || []).filter(c => c !== 'ninguna'),
                    ...(patient.custom_health_conditions || [])
                  ];

                  return (
                    <Card key={patient.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-4">
                        {patient.profile_picture ? (
                          <img 
                            src={patient.profile_picture} 
                            alt={patient.full_name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                            {patient.full_name?.[0]?.toUpperCase()}
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{patient.full_name}</h3>
                          <p className="text-sm text-gray-500">{patient.email}</p>
                          
                          {conditions.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {conditions.map((condition, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full"
                                >
                                  {condition}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                            <span>📦 {patientProductsCount} productos escaneados</span>
                            <span>📅 Desde {new Date(patient.created_date).toLocaleDateString('es-AR', { month: 'short', year: 'numeric' })}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="products" className="space-y-4">
            {patientProducts.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">No hay productos compartidos</h3>
                <p className="text-gray-600 text-sm">
                  Cuando tus pacientes escaneen productos, aparecerán aquí
                </p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {patientProducts.map((product) => {
                  const patient = myPatients.find(p => p.email === product.created_by);
                  
                  return (
                    <Card key={product.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex gap-3">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-20 h-20 rounded-lg object-cover" />
                        ) : (
                          <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
                          {product.brand && <p className="text-sm text-gray-500">{product.brand}</p>}
                          
                          <div className="flex items-center gap-2 mt-2">
                            {product.scaneat_score !== undefined && (
                              <div className={`
                                px-2 py-1 rounded-lg text-xs font-bold
                                ${product.traffic_light === 'green' ? 'bg-emerald-50 text-emerald-700' :
                                  product.traffic_light === 'yellow' ? 'bg-amber-50 text-amber-700' :
                                  'bg-red-50 text-red-700'}
                              `}>
                                {product.scaneat_score}
                              </div>
                            )}
                          </div>

                          {patient && (
                            <p className="text-xs text-gray-400 mt-2">
                              Paciente: {patient.full_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}